import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, hasPermission } from "@/lib/auth";
import Login from "@/pages/Login";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import ClientsList from "@/pages/ClientsList";
import ClientDetail from "@/pages/ClientDetail";
import ContractsList from "@/pages/ContractsList";
import Users from "@/pages/Users";
import Sales from "@/pages/Sales";
import Reports from "@/pages/Reports";
import Stocks from "@/pages/Stocks";
import StockFisa from "@/pages/StockFisa";
import FiseMagazie from "@/pages/FiseMagazie";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import ClientPortal from "@/pages/ClientPortal";
import Subscribe from "@/pages/Subscribe";
import AdminSync from "@/pages/AdminSync";
import InstallPrompt from "@/components/InstallPrompt";

function Protected({ children, adminOnly = false, perm = null }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="label-eyebrow text-muted-foreground">Se încarcă...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (perm && !hasPermission(user, perm)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  useEffect(() => {
    // Register service worker for PWA (offline + installable score).
    // Also auto-detect updates so the installed/downloaded app always shows the latest deploy.
    if ("serviceWorker" in navigator && window.location.protocol === "https:") {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((reg) => {
          // 1) Check for updates every time the app becomes visible (open from home screen)
          const check = () => reg.update().catch(() => {});
          document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") check();
          });
          // 2) Also poll every 5 min while open
          setInterval(check, 5 * 60 * 1000);
          // 3) When a new SW is waiting, activate it immediately and reload once
          const handleUpdate = () => {
            if (reg.waiting) reg.waiting.postMessage("SKIP_WAITING");
          };
          reg.addEventListener("updatefound", () => {
            const newSW = reg.installing;
            if (!newSW) return;
            newSW.addEventListener("statechange", () => {
              if (newSW.state === "installed" && navigator.serviceWorker.controller) {
                handleUpdate();
              }
            });
          });
          let refreshing = false;
          const doReload = () => {
            if (refreshing) return;
            refreshing = true;
            window.location.reload();
          };
          navigator.serviceWorker.addEventListener("controllerchange", doReload);
          navigator.serviceWorker.addEventListener("message", (e) => {
            if (e.data === "SW_UPDATED") doReload();
          });
        })
        .catch((err) => console.warn("SW registration failed:", err));
    }
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/client-portal" element={<ClientPortal />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/inregistreaza" element={<Subscribe />} />
            <Route path="/pricing" element={<Subscribe />} />
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/clienti" element={<ClientsList />} />
              <Route path="/clienti/:id" element={<ClientDetail />} />
              <Route
                path="/contracte"
                element={
                  <Protected perm="clients">
                    <ContractsList />
                  </Protected>
                }
              />
              <Route
                path="/vanzari"
                element={
                  <Protected perm="sales">
                    <Sales />
                  </Protected>
                }
              />
              <Route
                path="/rapoarte"
                element={
                  <Protected perm="reports">
                    <Reports />
                  </Protected>
                }
              />
              <Route
                path="/stocuri"
                element={
                  <Protected adminOnly>
                    <Stocks />
                  </Protected>
                }
              />
              <Route
                path="/stocuri/:id/fisa"
                element={
                  <Protected adminOnly>
                    <StockFisa />
                  </Protected>
                }
              />
              <Route
                path="/fise"
                element={
                  <Protected adminOnly>
                    <FiseMagazie />
                  </Protected>
                }
              />
              <Route
                path="/analiza"
                element={
                  <Protected adminOnly>
                    <AnalyticsDashboard />
                  </Protected>
                }
              />
              <Route
                path="/utilizatori"
                element={
                  <Protected adminOnly>
                    <Users />
                  </Protected>
                }
              />
              <Route
                path="/admin/sync"
                element={
                  <Protected adminOnly>
                    <AdminSync />
                  </Protected>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors closeButton position="top-right" />
        <InstallPrompt />
      </AuthProvider>
    </div>
  );
}

export default App;
